import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  AutoAwesome as AutoAwesomeIcon,
  Feedback as FeedbackIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const EmailPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    welcome_emails: true,
    onboarding_emails: true,
    activity_notifications: true,
    weekly_summaries: true,
    re_engagement_emails: true,
    marketing_emails: false,
    respondent_emails: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Email preference configurations with descriptions and icons
  const preferenceConfig = {
    welcome_emails: {
      label: 'Welcome Emails',
      description: 'Receive welcome messages when you join SurveySlack',
      icon: <AutoAwesomeIcon />,
      category: 'onboarding'
    },
    onboarding_emails: {
      label: 'Onboarding Emails',
      description: 'Get helpful tips and guidance to get started with creating surveys',
      icon: <EmailIcon />,
      category: 'onboarding'
    },
    activity_notifications: {
      label: 'Activity Notifications',
      description: 'Notifications about your survey activity, responses, and milestones',
      icon: <NotificationsIcon />,
      category: 'activity'
    },
    weekly_summaries: {
      label: 'Weekly Summaries',
      description: 'Weekly reports on your survey performance and response analytics',
      icon: <TrendingUpIcon />,
      category: 'activity'
    },
    re_engagement_emails: {
      label: 'Re-engagement Emails',
      description: 'Reminders and updates when you haven\'t used SurveySlack recently',
      icon: <PeopleIcon />,
      category: 'engagement'
    },
    respondent_emails: {
      label: 'Respondent Emails',
      description: 'Thank you emails and reminders for survey respondents',
      icon: <FeedbackIcon />,
      category: 'respondent'
    },
    marketing_emails: {
      label: 'Marketing Emails',
      description: 'Product updates, feature announcements, and promotional content',
      icon: <CampaignIcon />,
      category: 'marketing'
    }
  };

  // Group preferences by category
  const categorizedPreferences = {
    onboarding: ['welcome_emails', 'onboarding_emails'],
    activity: ['activity_notifications', 'weekly_summaries'],
    engagement: ['re_engagement_emails'],
    respondent: ['respondent_emails'],
    marketing: ['marketing_emails']
  };

  const categoryLabels = {
    onboarding: 'Getting Started',
    activity: 'Survey Activity',
    engagement: 'Re-engagement',
    respondent: 'Respondent Communications',
    marketing: 'Marketing & Updates'
  };

  useEffect(() => {
    loadPreferences();
  }, [user]);

  const loadPreferences = async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${getApiUrl('/get_email_preferences.php')}?user_id=${encodeURIComponent(user.uid)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.status === 'success') {
        setPreferences(data.data.preferences);
      } else {
        throw new Error(data.message || 'Failed to load email preferences');
      }
    } catch (err) {
      console.error('Error loading email preferences:', err);
      setError('Failed to load email preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = async (preferenceKey, value) => {
    if (!user?.uid) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Optimistically update the UI
      setPreferences(prev => ({
        ...prev,
        [preferenceKey]: value
      }));

      const response = await fetch(getApiUrl('/update_email_preferences.php'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          preferences: {
            [preferenceKey]: value
          }
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSuccess(true);
        // Update with the actual server response
        setPreferences(data.data.preferences);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        throw new Error(data.message || 'Failed to update email preferences');
      }
    } catch (err) {
      console.error('Error updating email preferences:', err);
      setError('Failed to update email preferences. Please try again.');
      
      // Revert the optimistic update
      setPreferences(prev => ({
        ...prev,
        [preferenceKey]: !value
      }));
    } finally {
      setSaving(false);
    }
  };

  const renderPreferenceGroup = (category, preferenceKeys) => (
    <Box key={category} sx={{ mb: 3 }}>
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2, 
          fontWeight: 500,
          color: 'text.primary',
          fontSize: '1.1rem'
        }}
      >
        {categoryLabels[category]}
      </Typography>
      
      <FormGroup>
        {preferenceKeys.map((key) => {
          const config = preferenceConfig[key];
          return (
            <Box key={key} sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences[key] || false}
                    onChange={(e) => handlePreferenceChange(key, e.target.checked)}
                    disabled={loading || saving}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', ml: 1 }}>
                    <Box sx={{ 
                      color: 'text.secondary', 
                      mr: 1.5, 
                      mt: 0.5,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {config.icon}
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {config.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {config.description}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ 
                  alignItems: 'flex-start',
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    flex: 1
                  }
                }}
              />
            </Box>
          );
        })}
      </FormGroup>
      
      {category !== 'marketing' && <Divider sx={{ mt: 2 }} />}
    </Box>
  );

  if (loading) {
    return (
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)' }}>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={24} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading email preferences...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      border: '1px solid rgba(0, 0, 0, 0.06)'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2, 
            fontWeight: 500, 
            display: 'flex', 
            alignItems: 'center' 
          }}
        >
          <EmailIcon sx={{ mr: 1 }} />
          Email Preferences
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Control which emails you receive from SurveySlack. You can update these preferences at any time.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Email preferences updated successfully!
          </Alert>
        )}

        {saving && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Saving preferences...
            </Typography>
          </Box>
        )}

        {Object.entries(categorizedPreferences).map(([category, keys]) =>
          renderPreferenceGroup(category, keys)
        )}

        <Box sx={{ 
          mt: 3, 
          p: 2, 
          bgcolor: 'grey.50', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> You can unsubscribe from all emails using the unsubscribe link 
            in any email we send you. Some transactional emails may still be sent for account security.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmailPreferences;